import { yupResolver } from '@hookform/resolvers/yup'
import { Button, FormLayout, TextInput } from 'components/common'
import { Switch } from 'components/common/switch'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import { updateOrderTrackerConfig } from 'utils/api/configApi'
import { handleError } from 'utils/helpers/handleError'
import {
  orderTrackerConfigSchema,
  OrderTrackerConfigFormFields,
} from 'utils/schema/orderTrackerConfigSchema'

// Defaults match the backend schema, for an empty configuration
const DEFAULTS: OrderTrackerConfigFormFields = {
  baseUrl: '',
  serviceApiToken: '',
  syncWebhookToken: '',
  cacheTtlSeconds: 60,
  requestTimeoutMs: 10000,
  allowLegacyLogin: true,
}

type Props = {
  configuration?: ConfigurationWithId
}

const OrderTrackerConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<OrderTrackerConfigFormFields>({
    resolver: yupResolver(orderTrackerConfigSchema),
    defaultValues: { ...DEFAULTS, ...(configuration?.pickerAuth || {}) },
  })

  const [loading, setLoading] = useState(false)

  const handleSave = async (data: OrderTrackerConfigFormFields) => {
    const configToast = toast.loading('Updating Order Tracker config...')
    setLoading(true)
    try {
      await updateOrderTrackerConfig(data)
      toast.success('Order Tracker config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update Order Tracker config.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* ================= CONNECTION ================= */}
      <h3 className="title mt-4">Connection</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name="baseUrl"
            label="Order Tracker Base URL"
            placeholder="https://order-tracker-api.example.com"
            error={errors?.baseUrl?.message}
          />
          <TextInput
            required
            type="password"
            register={register}
            name="serviceApiToken"
            label="Service API Token"
            hintText="Order Tracker API token with picker:introspect and picker:read scopes"
            error={errors?.serviceApiToken?.message}
          />
          <TextInput
            required
            type="password"
            register={register}
            name="syncWebhookToken"
            label="Picker Sync Token"
            hintText="Shared secret Order Tracker sends when pushing pickers. Same as AGGREGATOR_PICKER_SYNC_TOKEN there"
            error={errors?.syncWebhookToken?.message}
          />
          <TextInput
            required
            type="number"
            min={1000}
            step={1}
            register={register}
            name="requestTimeoutMs"
            label="Request Timeout (ms)"
            error={errors?.requestTimeoutMs?.message}
          />
        </div>
      </FormLayout>

      {/* ================= LOGIN ================= */}
      <h3 className="title mt-4">Store App Login</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            type="number"
            min={1}
            step={1}
            register={register}
            name="cacheTtlSeconds"
            label="Session Cache (seconds)"
            hintText="How long a checked new app login is reused. A deactivated picker keeps working up to this long"
            error={errors?.cacheTtlSeconds?.message}
          />
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Allow Old App Login
              </p>
              <p className="text-xs text-gray-500">
                Off: the old store app can no longer log in or be used
              </p>
            </div>
            <Controller
              name="allowLegacyLogin"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch checked={!!value} onChange={onChange} />
              )}
            />
          </div>
        </div>
      </FormLayout>

      <div className="mt-6 flex justify-end">
        <Button
          type="submit"
          buttonText="Save"
          loading={loading}
          disabled={loading}
        />
      </div>
    </form>
  )
}

export default OrderTrackerConfigurationForm
